import React, { useEffect, useRef } from 'react';
import theyyam1 from '../assets/kavuotherimg/theyyam1.webp';
import theyyam2 from '../assets/kavuotherimg/theyyam2.webp';
import theyyam3 from '../assets/kavuotherimg/theyyam3.webp';
import theyyam4 from '../assets/kavuotherimg/theyyam4.webp';

/**
 * A custom React hook that uses the Intersection Observer API
 * to detect when an element enters the viewport. It applies a
 * 'is-visible' class to trigger animations.
 *
 * @param {object} options - Configuration options for the IntersectionObserver.
 * @returns {React.RefObject} A ref object to be attached to the container element.
 */
const useIntersectionObserver = (options) => {
    const containerRef = useRef(null);

    useEffect(() => {
        // Create the observer with a callback function
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                // If the element is intersecting (i.e., visible)
                if (entry.isIntersecting) {
                    // Add the class to trigger the CSS animation
                    entry.target.classList.add('is-visible');
                    // Stop observing the element so the animation only runs once
                    observer.unobserve(entry.target);
                }
            });
        }, options);

        const currentRef = containerRef.current;
        if (currentRef) {
            // Find all elements with the '.image-block' class within the container
            const elements = currentRef.querySelectorAll('.image-block');
            // Start observing each of them
            elements.forEach(el => observer.observe(el));
        }

        // Cleanup function: unobserve all elements when the component unmounts
        return () => {
            if (currentRef) {
                const elements = currentRef.querySelectorAll('.image-block');
                elements.forEach(el => observer.unobserve(el));
            }
        };
    }, [options]); // Re-run the effect if the options change

    return containerRef;
};


/**
 * The Theyyam component displays information about the various Theyyams
 * performed at the temple, using an animated grid layout.
 *
 * @param {object} props - The component's properties.
 * @param {function} props.openImageViewer - A function passed from the parent to open the fullscreen image viewer.
 */
const Theyyam = ({ openImageViewer }) => {
    // Get the ref from the custom hook to attach to the grid container
    const theyyamGridRef = useIntersectionObserver({ threshold: 0.2 });

    /**
     * Handles clicks on the image grid. It uses event delegation to find
     * the clicked image's source and calls the openImageViewer function.
     * @param {React.MouseEvent} e - The mouse event.
     */
    const handleImageClick = (e) => {
        // Find the closest parent element with the 'clickable-image' class
        const clickableItem = e.target.closest('.clickable-image');
        // If found, get the 'src' from the 'img' tag inside it
        const imgSrc = clickableItem?.querySelector('img')?.src;
        
        // If a valid image source is found, open the viewer
        if (imgSrc) {
            openImageViewer(imgSrc);
        }
    };

    return (
        // The main container for the "Theyyam" page section
        <div className="page" id="page-theyyam">
            <h2 className="content-title">തെയ്യം</h2>
            
            {/* The grid container. The ref is attached here, and the click handler uses event delegation. */}
            <div className="theyyam-grid" ref={theyyamGridRef} onClick={handleImageClick}>
                
                {/* Grid Item 1: Image */}
                <div className="theyyam-item image-block clickable-image grid-row-span-2">
                    <img src={theyyam1} alt="വിഷ്ണുമൂർത്തി തെയ്യം" className="theyyam-image" />
                </div>
                
                {/* Grid Item 2: Text */}
                <div className="theyyam-item text-block grid-col-span-3">
                    <h3>തെയ്യങ്ങൾ</h3>
                    <p>തലയന്നേരി കാവിലെ സാക്ഷാൽ പള്ളിയറയിൽ ശ്രീ പൂമാല ഭഗവതിയും ശ്രീ കുറുമ്പാ ഭഗവതിയുമാണ് പ്രതിഷ്ഠ, വലതുഭാഗത്തുള്ള മാടത്തിൽ ശ്രീ പൂമാരുതനും. കന്നിമൂലയിലെ അണിയറയിൽ വിഷ്ണുമൂർത്തി. രക്തചാമുണ്ഡി. മടയിൽ ചാമുണ്ഡി പുതിയ ഭഗവതി കന്നിക്കൊരു മകൻ.വീരനും.മണികണ്ഠനും എന്നീ തെയ്യങ്ങളാണ് കുടികൊള്ളുന്നത്. ശ്രീ പൂമാല ഭഗവതിക്കും കൂരമ്പഭഗവതിക്കും പ്രിയ കോലങ്ങൾ ഇല്ല                                വടക്കേം വാതിൽ കളിയാട്ടം: പതിറ്റാണ്ടുകളുടെ ഇടവേളയിലാണ് വടക്കേം വാതിൽ കളിയാട്ടം നടത്തിവരുന്നത് പൂമാരുതൻ ഒഴികെയുള്ള എല്ലാ തെയ്യങ്ങളും വടക്കേ പടിപ്പുരയോട് ചേർന്ന് പതി കെട്ടിയാണ് കെട്ടിയാടുന്നത്.</p>
                </div>
                
                {/* Grid Item 3: Image */}
                <div className="theyyam-item image-block clickable-image grid-col-span-2">
                    <img src={theyyam2} alt="പുതിയ ഭഗവതി തെയ്യം" className="theyyam-image" />
                </div>
                
                {/* Grid Item 4: Image */}
                <div className="theyyam-item image-block clickable-image">
                    <img src={theyyam3} alt="രക്തചാമുണ്ഡി തെയ്യം" className="theyyam-image" />
                </div>
                
                {/* Grid Item 5: Text */}
                <div className="theyyam-item text-block grid-col-span-4">
                    <h3>നടക്ക് താഴെ കളിയാട്ടം</h3>
                    <p>  നാട്ടിൽ മാറാരോഗങ്ങൾ പിടിപെട്ടാൽ രോഗശാന്തിക്ക് വേണ്ടിയാണ് നടക്കു താഴെയുള്ള കളിയാട്ടം നടത്തിവരുന്നത് ഇവിടെ കെട്ടിയാടുന്ന തെയ്യങ്ങൾ പ്രധാനമായും വിഷ്ണുമൂർത്തി (ഒറ്റക്കോലം) പുതിയ ഭഗവതി അങ്കക്കുളങ്ങര ഭഗവതി. രക്തചാമുണ്ഡി. പനിയൻ എന്നിവയാണ് വിഷ്ണുമൂർത്തിയും, പുതിയ ഭഗവതിയും മാരിമാറ്റാൻ പോകുന്ന ഒരു ചടങ്ങുണ്ട്. പുതിയ ഭഗവതി വടക്കോട്ട് പുറപ്പെട്ട് മുങ്ങം. അഞ്ചരിപ്പാട്, 500 പതീരെ കൊവ്വൽ എന്നിവിടങ്ങളിൽ ചെന്ന് മാരി മാറ്റിയതിന് ശേഷം തിരിച്ചു വരും. വിഷ്ണുമൂർത്തി തെക്കോട്ട് പുറപ്പെട്ട കുറിഞ്ഞി ക്ഷേത്രത്തിൽ വന്ദിച്ച് നാരങ്ങ തോടിന് സമീപത്തുള്ള മാരിക്കുളത്തിൽ ചെന്ന് മാരിമാറ്റി തിരിച്ചു വരുന്നു. ക്ഷേത്രത്തിൽ തിരിച്ചെത്തിയ രണ്ട് തെയ്യങ്ങളും വെളിച്ചപ്പാടും സ്ഥാനികരും വാല്യക്കാരും തട്ടാർ പുഴക്കരികിലുള്ള മാരിക്കുളത്തിൽ ചെന്ന് ആചാര അനുഷ്ഠാനങ്ങളോടെ മാരിമാറ്റൽ ചടങ്ങു കഴിഞ്ഞ് ക്ഷേത്രസന്നിധിയിലേക്ക് തിരിച്ചുവന്ന് ഭക്തജനങ്ങൾക്ക് ദർശനമേകുന്നു                               ഇവിടത്തെ തെയ്യക്കോലങ്ങളുടെ അവകാശികൾ കരിവെള്ളൂർ പെരുമലയൻ കുടുംബാംഗങ്ങളും വണ്ണാൻ സമുദായത്തിലെ കാങ്കോലാൻ അന്നൂരാൻ കുടുംബാംഗങ്ങളുമാണ്.</p>
                </div>
                
                {/* Grid Item 6: Text */}
                <div className="theyyam-item text-block grid-col-span-2">
                    <h3>വടക്കേം വാതിൽ കളിയാട്ടം</h3>
                    <p>പതിറ്റാണ്ടുകളുടെ ഇടവേളയിലാണ് വടക്കേം വാതിൽ കളിയാട്ടം നടത്തിവരുന്നത്. പൂമാരുതൻ ഒഴികെയുള്ള എല്ലാ തെയ്യങ്ങളും വടക്കേ പടിപ്പുരയോട് ചേർന്ന് പതി കെട്ടിയാണ് കെട്ടിയാടുന്നത്.</p>
                </div>
                
                {/* Grid Item 7: Image */}
                <div className="theyyam-item image-block clickable-image grid-col-span-2">
                    <img src={theyyam4} alt="അനുഷ്ഠാനം" className="theyyam-image" />
                </div>
            </div>
        </div>
    );
};

export default Theyyam;
